import { binarySearchNumeric } from "@thi.ng/arrays";
import { getNumericComparator, chainComparators } from "utils/collect";
const CONDITION_COMPARATOR = chainComparators([
    getNumericComparator((c) => c.id),
    getNumericComparator((c) => c.value)
]);
const DEFAULT_IDS = Array.from(Array(100).keys());
const DEFAULT_VALUES = Array.from(Array(100).keys());
export class Conditions {
    static EMPTY = [];
    static INSTANCES = DEFAULT_IDS.map((id) => DEFAULT_VALUES.map((value) => ({ id, value })));
    static isValid(condition) {
        return condition.id >= 0 && condition.value >= 0;
    }
    static equals(a, b) {
        if (a === undefined) {
            return b === undefined;
        }
        else if (b === undefined) {
            return false;
        }
        return a.id == b.id && a.value == b.value;
    }
    static compare(a, b) {
        return CONDITION_COMPARATOR(a, b);
    }
    static create(id, value) {
        const cached = this.INSTANCES?.[id]?.[value];
        if (cached !== undefined) {
            return cached;
        }
        return { id, value };
    }
    static emptyBuilder() {
        return new ConditionSetBuilder([]);
    }
    static emptySet() {
        return Conditions.EMPTY;
    }
    static single(id, value) {
        return this.singleton(Conditions.create(id, value));
    }
    static singleton(condition) {
        return [condition];
    }
    static from(conditions) {
        if (conditions === undefined) {
            return Conditions.emptySet();
        }
        const builder = Conditions.emptyBuilder();
        for (const condition of conditions) {
            builder.addCondition(condition);
        }
        return builder.build();
    }
}
export function getConditionIds(conditions) {
    return conditions.map((c) => c.id);
}
export function getConditionByIndex(conditions, index) {
    return conditions[index];
}
export function getConditionIndexById(conditions, id) {
    for (let i = 0; i < conditions.length; i++) {
        if (conditions[i].id == id) {
            return i;
            // if we pass the id we know it's not there
        }
        else if (conditions[i].id > id) {
            return -1;
        }
    }
    return -1;
}
export function getConditionById(conditions, id) {
    return conditions[getConditionIndexById(conditions, id)];
}
export function conditionsHasId(conditions, id) {
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < conditions.length; i++) {
        if (conditions[i].id == id) {
            return true;
            // if we pass the id we know it's not there
        }
        else if (conditions[i].id > id) {
            return false;
        }
    }
    return false;
}
export function conditionsIsEmpty(conditions) {
    return conditionsSize(conditions) == 0;
}
export function conditionsSize(conditions) {
    return conditions.length;
}
export function conditionsContains(conditions, condition) {
    return Conditions.equals(condition, getConditionById(conditions, condition.id));
}
export function conditionsContainsAll(conditions, other) {
    return conditions.length == 0 || other.every((c) => conditionsContains(conditions, c));
}
export function conditionsContainsSome(conditions, other) {
    return conditions.length == 0 || other.some((c) => conditionsContains(conditions, c));
}
export function conditionsEquals(conditions, other) {
    return (other !== undefined &&
        conditionsContainsAll(conditions, other) &&
        conditionsSize(conditions) == conditionsSize(other));
}
export function copyConditions(conditions) {
    return conditions.slice();
}
export function copyConditionsAndRemove(conditions, condition) {
    return conditions.filter((c) => !Conditions.equals(c, condition));
}
export function copyConditionsAndRemoveAll(conditions, other) {
    if (conditionsIsEmpty(other)) {
        return conditions;
    }
    return conditions.filter((c) => !conditionsContains(other, c));
}
export function copyConditionsAndAdd(conditions, id, value) {
    const condition = Conditions.create(id, value);
    if (conditions.length == 0) {
        return [condition];
    }
    else if (id < conditions[0].id) {
        return [condition].concat(conditions);
    }
    else if (conditions[conditions.length - 1].id < id) {
        return conditions.concat([condition]);
    }
    const update = conditions.slice();
    updateConditionsAndAdd(update, condition);
    return update;
}
export function updateConditionsAndAdd(conditions, condition) {
    for (let i = 0; i < conditions.length; i++) {
        if (conditions[i].id == condition.id) {
            conditions[i] = condition;
            return i;
        }
        else if (conditions[i].id > condition.id) {
            conditions.splice(i, 0, condition);
            return i;
        }
    }
    conditions.push(condition);
    return conditions.length - 1;
}
class ConditionSetBuilder {
    conditions;
    constructor(conditions) {
        this.conditions = conditions;
    }
    addCondition(condition) {
        for (let i = 0; i < this.conditions.length; i++) {
            if (this.conditions[i].id == condition.id) {
                this.conditions[i] = condition;
                return this;
            }
            else if (this.conditions[i].id > condition.id) {
                this.conditions.splice(i, 0, condition);
                return this;
            }
        }
        this.conditions.push(condition);
        return this;
    }
    addConditions(conditions) {
        conditions.forEach((condition) => this.addCondition(condition));
        return this;
    }
    addValue(id, value) {
        return this.addCondition({ id, value });
    }
    removeId(id) {
        for (let i = 0; i < this.conditions.length; i++) {
            if (this.conditions[i].id == id) {
                this.conditions.splice(i, 1);
                return this;
            }
            else if (this.conditions[i].id < id) {
                return this;
            }
        }
        return this;
    }
    removeCondition(condition) {
        return this.removeId(condition.id);
    }
    removeConditions(conditions) {
        conditions.forEach((condition) => this.removeCondition(condition));
        return this;
    }
    build() {
        if (this.conditions.length == 0) {
            return Conditions.emptySet();
        }
        return this.conditions.slice();
    }
}
export class ConditionSetFactory {
    conditionFactoryMap;
    constructor(conditionFactoryMap) {
        this.conditionFactoryMap = conditionFactoryMap;
    }
    getId(column) {
        const conditionFactory = this.conditionFactoryMap[column];
        if (conditionFactory !== undefined) {
            return conditionFactory.getId();
        }
        return undefined;
    }
    getCondition(column, rawValue) {
        const conditionFactory = this.conditionFactoryMap[column];
        if (conditionFactory !== undefined) {
            const id = conditionFactory.getId();
            const value = conditionFactory.getValue(rawValue);
            if (value !== undefined) {
                return Conditions.create(id, value);
            }
        }
        return undefined;
    }
    getConditionSet(data = {}) {
        const builder = Conditions.emptyBuilder();
        for (const column of Object.keys(data)) {
            const conditionFactory = this.conditionFactoryMap[column];
            if (conditionFactory !== undefined) {
                const id = conditionFactory.getId();
                const value = conditionFactory.getValue(data[column]);
                if (value !== undefined) {
                    builder.addValue(id, value);
                }
            }
        }
        return builder.build();
    }
}
export class ConditionFactory {
    id;
    constructor(id) {
        this.id = id;
    }
    getId() {
        return this.id;
    }
    getCondition(x) {
        const value = this.getValue(x);
        if (value !== undefined) {
            return Conditions.create(this.id, value);
        }
    }
}
export class CategoricalConditionFactory extends ConditionFactory {
    values;
    valuesReverseIndex;
    otherValue;
    missingValue;
    constructor(id, values, valuesReverseIndex, otherValue, missingValue) {
        super(id);
        this.values = values;
        this.valuesReverseIndex = valuesReverseIndex;
        this.otherValue = otherValue;
        this.missingValue = missingValue;
    }
    getValue(x) {
        if (x !== null && x !== undefined) {
            const value = this.valuesReverseIndex[x];
            if (value === undefined && !isNaN(Number(x))) {
                const match = this.values.find((value) => Number(value) == Number(x));
                if (match !== undefined) {
                    return this.getValue(match);
                }
            }
            if (value !== undefined) {
                return value;
            }
            else if (this.otherValue !== undefined) {
                return this.valuesReverseIndex[this.otherValue] ?? undefined;
            }
        }
        else if (this.missingValue !== undefined) {
            return this.valuesReverseIndex[this.missingValue] ?? undefined;
        }
        return undefined;
    }
    getRawValue(value) {
        const raw = this.values[value];
        if (raw === undefined) {
            throw new Error(`Invalid value: ${value}. Expected an integer between 0 and ${this.values.length - 1}`);
        }
        return raw;
    }
    static isMissing(x) {
        return x === null || x === undefined || x === "{Missing}";
    }
    static create(id, values, otherValue, missingValue = "{Missing}") {
        const reverseIndex = values.reduce((obj, value, index) => {
            obj[value] = index;
            return obj;
        }, {});
        return new CategoricalConditionFactory(id, values, reverseIndex, otherValue, missingValue);
    }
}
export class NumericConditionFactory extends ConditionFactory {
    bins;
    hasMissing;
    constructor(id, bins, hasMissing) {
        super(id);
        this.bins = bins;
        this.hasMissing = hasMissing;
    }
    getValue(x) {
        const parsed = NumericConditionFactory.parse(x);
        if (parsed === undefined || parsed === null) {
            return this.hasMissing ? this.bins.length - 1 : undefined;
        }
        else if (isFinite(parsed) || parsed == Infinity || parsed == -Infinity) {
            const result = binarySearchNumeric(this.bins, parsed);
            // If the parsed value is equal to the value of the first bin, return 0 to fix first value referencing the wrong bin with binarySearchNumeric
            const firstValue = this.bins[0];
            if (parsed === firstValue) {
                return 0;
            }
            if (result < 0) {
                const insertionIndex = -(result + 1);
                if (insertionIndex == 0) {
                    return 0;
                }
                else if (insertionIndex == this.bins.length) {
                    return insertionIndex - 2;
                }
                else {
                    return insertionIndex - 1;
                }
            }
            else if (result < this.bins.length - 1) {
                return result;
            }
            else {
                return this.bins.length - 2;
            }
        }
        return undefined;
    }
    getRawValue(value) {
        if (value == this.bins.length - 1 && this.hasMissing) {
            return undefined;
        }
        const raw = this.bins[value + 1];
        if (raw === undefined) {
            throw new Error(`Invalid value: ${value}. Expected an integer between 0 and ${this.bins.length - (this.hasMissing ? 1 : 2)}`);
        }
        return raw;
    }
    static parse(x) {
        if (x === undefined || x === null || typeof x === "number") {
            return x;
        }
        else if (CategoricalConditionFactory.isMissing(x)) {
            return undefined;
        }
        const parsed = Number(x);
        if (!isNaN(parsed)) {
            return parsed;
        }
        // legacy handling
        const split = x.replace("more than ", "").split(" to ");
        if (split.length == 2) {
            const [first, second] = split.map((x) => {
                const val = x.trim();
                if (val === "inf") {
                    return Infinity;
                }
                else if (val == "-inf") {
                    return -Infinity;
                }
                else {
                    return Number(val);
                }
            });
            if (isFinite(first)) {
                return isFinite(second) ? (second + first) / 2 : first;
            }
            else if (first === -Infinity && second === Infinity) {
                return 0;
            }
            else if (isFinite(second)) {
                return second;
            }
        }
        return NaN;
    }
}
